import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  message,
  Tag,
  Space,
  Typography,
  Steps,
  Progress,
  Tabs,
  Timeline,
  Descriptions,
  Statistic,
  Alert,
  Spin,
} from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  RocketOutlined,
  FormOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../store';
import {
  fetchApplications,
  submitApplication,
  fetchOnboardingForm,
} from '../store/slices/onboardingSlice';
import { OnboardingApplication, KycStatusResult } from '../types';
import ApiService from '../services/api';
import KycService from '../services/kyc';
import CustomOnboardingForm from '../components/CustomOnboardingForm';
import ConnectVerificationCard from '../components/ConnectVerificationCard';
import OnboardingRequirementsCard from '../components/OnboardingRequirementsCard';

const { Title, Text } = Typography;
const { Step } = Steps;
const { TabPane } = Tabs;

// Stripe Connect 支持的国家代码列表（label 在渲染时按 i18n 翻译）
const SUPPORTED_COUNTRY_CODES = [
  'US', 'GB', 'CA', 'AU', 'DE', 'FR', 'ES', 'IT', 'NL', 'JP', 'SG', 'HK',
];

// MoR 模式 KYC 状态 Tag 配置（text 在渲染时按 i18n 翻译）
const KYC_STATUS_TAG: Record<string, { color: string; key: string }> = {
  not_started: { color: 'default', key: 'kycNotStarted' },
  pending: { color: 'processing', key: 'kycPending' },
  approved: { color: 'success', key: 'kycApproved' },
  rejected: { color: 'error', key: 'kycRejected' },
};

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { applications, form, submitting, loading } = useSelector(
    (state: RootState) => state.onboarding
  );

  const [activeTab, setActiveTab] = useState('applications');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<OnboardingApplication | null>(null);
  const [applicationForm] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [kycInfo, setKycInfo] = useState<KycStatusResult | null>(null);
  // KYC 状态是否已拉取完成（用于决定按 mode 渲染前的兜底展示）
  const [modeResolved, setModeResolved] = useState(false);

  // 商户 mode 决定入驻流程：
  //   MoR（代收）→ 平台 KYC（Didit）入驻；
  //   PSP        → 国际收单通道（Stripe Connect）入驻 + 账户验证补充。
  // mode 来源：GET /merchant/kyc/status 的 mode 字段。
  // 兜底：拉取失败或暂未返回时，默认按 MoR/KYC 展示（绝大多数商户为 MoR）。
  // TODO: 若后续 mode 可从 auth user / merchant profile 直接获取，可省去此请求。
  const isMorMode = kycInfo ? kycInfo.mode === 'mor' : true;

  // 是否存在 Stripe Connect（国际收单通道）入驻申请
  const hasConnectApplication = (applications.data || []).some(
    (app) => app.provider_name === 'stripe_connect'
  );

  useEffect(() => {
    dispatch(fetchApplications({ page: 1, limit: 20 }));
  }, [dispatch]);

  // 获取 KYC 状态（用于判断商户 mode）
  useEffect(() => {
    KycService.getKycStatus()
      .then(setKycInfo)
      .catch(() => {
        // 获取失败时静默处理，按 MoR/KYC 兜底展示
      })
      .finally(() => setModeResolved(true));
  }, []);

  // PSP 快速入驻 - 提交国际收单通道入驻申请
  // 不再让商户选择插件/渠道：后端按商户 mode（PSP）自动决定 plugin（stripe_connect）。
  const handleQuickOnboarding = async (values: any) => {
    setFormLoading(true);
    try {
      await ApiService.post('/merchant/onboarding/submit', {
        email: values.email,
        country: values.country,
        business_type: values.business_type,
      });

      // 白标：不跳转托管页。提交后关闭弹窗并刷新列表，
      // 后续如需补充验证资料，在「账户验证」区块内本平台填写提交。
      message.success(t('onboarding.submitSuccess'));
      setCreateModalVisible(false);
      applicationForm.resetFields();
      dispatch(fetchApplications({ page: 1, limit: 20 }));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('onboarding.submitFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewApplication = (record: OnboardingApplication) => {
    setSelectedApplication(record);
    setDetailModalVisible(true);
  };

  const statusConfig = {
    'pending': { color: 'processing', text: t('onboarding.statusPending'), icon: <ClockCircleOutlined /> },
    'under_review': { color: 'warning', text: t('onboarding.statusUnderReview'), icon: <ClockCircleOutlined /> },
    'approved': { color: 'success', text: t('onboarding.statusApproved'), icon: <CheckCircleOutlined /> },
    'rejected': { color: 'error', text: t('onboarding.statusRejected'), icon: <CloseCircleOutlined /> },
    'incomplete': { color: 'error', text: t('onboarding.statusIncomplete'), icon: <ExclamationCircleOutlined /> },
  };

  const columns = [
    {
      title: t('onboarding.applicationId'),
      dataIndex: 'id',
      key: 'id',
      width: 160,
      render: (text: string) => (
        <Button
          type="link"
          onClick={() => handleViewApplication({ id: text } as OnboardingApplication)}
        >
          #{text.substring(0, 12)}...
        </Button>
      ),
    },
    {
      title: t('onboarding.provider'),
      dataIndex: 'provider_name',
      key: 'provider_name',
      width: 120,
      render: (text: string) => (
        <Tag color={text === 'stripe_connect' ? 'geekblue' : 'blue'}>
          {text === 'stripe_connect' ? t('onboarding.intlAcquiringChannel') : text}
        </Tag>
      ),
    },
    {
      title: t('onboarding.submitDate'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: t('onboarding.reviewStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: t('onboarding.channelAccount'),
      dataIndex: 'external_id',
      key: 'external_id',
      width: 160,
      render: (text: string) => text ? (
        <Text code style={{ fontSize: 12 }}>{text}</Text>
      ) : '-',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      render: (_: any, record: OnboardingApplication) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewApplication(record)}
          >
            {t('common.view')}
          </Button>
        </Space>
      ),
    },
  ];

  const onboardingSteps = [
    { title: t('onboarding.step1Title'), description: t('onboarding.step1Desc') },
    { title: t('onboarding.step2Title'), description: t('onboarding.step2Desc') },
    { title: t('onboarding.step3Title'), description: t('onboarding.step3Desc') },
    { title: t('onboarding.step4Title'), description: t('onboarding.step4Desc') },
    { title: t('onboarding.step5Title'), description: t('onboarding.step5Desc') },
  ];

  const kycTag = kycInfo
    ? (KYC_STATUS_TAG[kycInfo.kyc_status]
        ? { color: KYC_STATUS_TAG[kycInfo.kyc_status].color, text: t(`onboarding.${KYC_STATUS_TAG[kycInfo.kyc_status].key}`) }
        : { color: 'default', text: kycInfo.kyc_status })
    : null;

  // 申请列表表格（MoR / PSP 共用）
  const applicationsTable = (
    <Table
      columns={columns}
      dataSource={applications.data}
      rowKey="id"
      loading={applications.loading}
      pagination={{
        current: applications.pagination?.current_page || 1,
        pageSize: applications.pagination?.per_page || 20,
        total: applications.pagination?.total || 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('onboarding.applicationsTotal', { total }),
      }}
    />
  );

  // ===== MoR 入驻视图：平台 KYC（Didit），不展示任何插件/渠道选择与 Stripe Connect =====
  const renderMorView = () => (
    <>
      <Alert
        message={t('onboarding.morAlertTitle')}
        description={t('onboarding.morAlertDesc')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 页面头部 */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div className="header-content">
          <Title level={2}>
            <SafetyCertificateOutlined style={{ marginRight: 12, color: '#2F6BFF' }} />
            {t('onboarding.morTitle')}
          </Title>
          <Text type="secondary">{t('onboarding.morSubtitle')}</Text>
        </div>
      </div>

      {/* 平台实名认证（KYC）状态与入口 */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space size="large" wrap>
            <Space>
              <SafetyCertificateOutlined style={{ fontSize: 24, color: '#2F6BFF' }} />
              <Text strong>{t('onboarding.platformKyc')}</Text>
            </Space>
            <Space>
              <Text type="secondary">{t('onboarding.currentStatus')}</Text>
              {kycTag && <Tag color={kycTag.color}>{kycTag.text}</Tag>}
            </Space>
            <Button
              type="primary"
              size="large"
              icon={<SafetyCertificateOutlined />}
              onClick={() => navigate('/kyc')}
            >
              {kycInfo?.kyc_status === 'approved'
                ? t('onboarding.viewKycDetail')
                : t('onboarding.goToKyc')}
            </Button>
          </Space>
          <Text type="secondary">{t('onboarding.morKycHint')}</Text>
        </Space>
      </Card>

      {/* 入驻要求（必填项 / 证件 / 验证环节） */}
      <OnboardingRequirementsCard />

      {/* 认证申请记录 */}
      <Card title={t('onboarding.tabApplications')}>{applicationsTable}</Card>
    </>
  );

  // ===== PSP 入驻视图：国际收单通道（Stripe Connect）+ 白标账户验证 =====
  const renderPspView = () => (
    <>
      {/* Stripe Connect 白标账户验证（存在 Connect 申请时显示） */}
      <ConnectVerificationCard hasConnectApplication={hasConnectApplication} />

      {/* 页面头部 */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div className="header-content">
          <Title level={2}>
            <RocketOutlined style={{ marginRight: 12, color: '#2F6BFF' }} />
            {t('onboarding.title')}
          </Title>
          <Text type="secondary">{t('onboarding.subtitle')}</Text>
        </div>
        <div className="header-actions" style={{ marginTop: 16 }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => dispatch(fetchApplications({ page: 1, limit: 20 }))}>
              {t('common.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setCreateModalVisible(true);
              }}
              size="large"
            >
              {t('onboarding.quickOnboard')}
            </Button>
          </Space>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('onboarding.totalApplications')}
              value={applications.data?.length || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#2F6BFF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('onboarding.pendingCount')}
              value={applications.data?.filter(app => app.status === 'pending').length || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('onboarding.approvedCount')}
              value={applications.data?.filter(app => app.status === 'approved').length || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('onboarding.onboardChannel')}
              value={t('onboarding.intlAcquiringChannel')}
              valueStyle={{ color: '#2F6BFF', fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 主要内容 */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={t('onboarding.tabApplications')} key="applications">
            {applicationsTable}
          </TabPane>

          <TabPane tab={t('onboarding.tabProcess')} key="process">
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={14}>
                <Card title={t('onboarding.processTitle')} extra={<Tag color="blue">{t('onboarding.recommended')}</Tag>}>
                  <Steps direction="vertical" current={-1}>
                    {onboardingSteps.map((step, index) => (
                      <Step
                        key={index}
                        title={step.title}
                        description={step.description}
                      />
                    ))}
                  </Steps>
                  <div style={{ marginTop: 24 }}>
                    <Space>
                      <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={() => {
                          setCreateModalVisible(true);
                        }}
                      >
                        {t('onboarding.quickOnboardRedirect')}
                      </Button>
                      <Button
                        size="large"
                        icon={<FormOutlined />}
                        onClick={() => setActiveTab('custom')}
                      >
                        {t('onboarding.fullOnboardLocal')}
                      </Button>
                    </Space>
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={10}>
                <Card title={t('onboarding.advantagesTitle')}>
                  <ul style={{ paddingLeft: 20, lineHeight: 2.5 }}>
                    <li>{t('onboarding.advantage1')}</li>
                    <li>{t('onboarding.advantage2')}</li>
                    <li>{t('onboarding.advantage3')}</li>
                    <li>{t('onboarding.advantage4')}</li>
                    <li>{t('onboarding.advantage5')}</li>
                    <li>{t('onboarding.advantage6')}</li>
                    <li>{t('onboarding.advantage7')}</li>
                  </ul>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab={t('onboarding.tabCustom')} key="custom">
            <Alert
              message={t('onboarding.fullModeTitle')}
              description={t('onboarding.fullModeDesc')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <CustomOnboardingForm
              onSuccess={() => {
                dispatch(fetchApplications({ page: 1, limit: 20 }));
                setActiveTab('applications');
              }}
              onCancel={() => setActiveTab('applications')}
            />
          </TabPane>
        </Tabs>
      </Card>
    </>
  );

  return (
    <div className="onboarding-page">
      {/* mode 未拉取完成前先占位，避免先闪 PSP 视图再切回 MoR */}
      {!modeResolved ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      ) : isMorMode ? (
        renderMorView()
      ) : (
        renderPspView()
      )}

      {/* PSP 快速入驻模态框（仅 PSP 视图触发；MoR 视图不展示入口） */}
      <Modal
        title={
          <Space>
            <RocketOutlined style={{ color: '#2F6BFF' }} />
            <span>{t('onboarding.quickOnboardModalTitle')}</span>
          </Space>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
        }}
        footer={null}
        width={500}
      >
        {(
          <Spin spinning={formLoading}>
            <Alert
              message={t('onboarding.quickOnboardNoteTitle')}
              description={t('onboarding.quickOnboardNoteDesc')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form
              form={applicationForm}
              layout="vertical"
              onFinish={handleQuickOnboarding}
            >
              <Form.Item
                name="email"
                label={t('onboarding.emailAddress')}
                rules={[
                  { required: true, message: t('onboarding.emailRequired') },
                  { type: 'email', message: t('onboarding.emailInvalid') }
                ]}
              >
                <Input placeholder="your@email.com" size="large" />
              </Form.Item>

              <Form.Item
                name="country"
                label={t('onboarding.country')}
                rules={[{ required: true, message: t('onboarding.countryRequired') }]}
              >
                <Select placeholder={t('onboarding.country')} size="large">
                  {SUPPORTED_COUNTRY_CODES.map(code => (
                    <Select.Option key={code} value={code}>
                      {t(`onboarding.country${code}`)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="business_type"
                label={t('onboarding.businessType')}
                rules={[{ required: true, message: t('onboarding.businessTypeRequired') }]}
              >
                <Select placeholder={t('onboarding.businessType')} size="large">
                  <Select.Option value="individual">{t('onboarding.businessIndividual')}</Select.Option>
                  <Select.Option value="company">{t('onboarding.businessCompany')}</Select.Option>
                  <Select.Option value="non_profit">{t('onboarding.businessNonProfit')}</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={formLoading}
                  size="large"
                  block
                  icon={<RocketOutlined />}
                >
                  {t('onboarding.startOnboard')}
                </Button>
              </Form.Item>
            </Form>
          </Spin>
        )}
      </Modal>

      {/* 申请详情模态框 */}
      <Modal
        title={t('onboarding.applicationDetail')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedApplication && (
          <div>
            <Descriptions title={t('onboarding.onboardInfo')} bordered column={2}>
              <Descriptions.Item label={t('onboarding.applicationId')} span={2}>
                {selectedApplication.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>
                {statusConfig[selectedApplication.status as keyof typeof statusConfig]?.text || selectedApplication.status}
              </Descriptions.Item>
              <Descriptions.Item label={t('onboarding.provider')}>
                {selectedApplication.provider_name === 'stripe_connect' ? t('onboarding.intlAcquiringChannel') : selectedApplication.provider_name}
              </Descriptions.Item>
              <Descriptions.Item label={t('onboarding.channelAccount')} span={2}>
                {selectedApplication.external_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('onboarding.submitTime')} span={2}>
                {new Date(selectedApplication.created_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            {selectedApplication.status === 'pending' && (
              <div style={{ marginTop: 24 }}>
                <Alert
                  message={t('onboarding.notCompletedTitle')}
                  description={t('onboarding.notCompletedDesc')}
                  type="warning"
                  showIcon
                />
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <Title level={5}>{t('onboarding.statusTimeline')}</Title>
              <Timeline>
                <Timeline.Item color="blue">
                  {t('onboarding.timelineSubmitted')} - {new Date(selectedApplication.created_at).toLocaleString()}
                </Timeline.Item>
                {selectedApplication.status === 'approved' && (
                  <Timeline.Item color="green">
                    {t('onboarding.timelineApproved')}
                  </Timeline.Item>
                )}
                {selectedApplication.status === 'rejected' && (
                  <Timeline.Item color="red">
                    {t('onboarding.timelineRejected')}
                  </Timeline.Item>
                )}
              </Timeline>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OnboardingPage;
